// web/src/api/turn_stream.ts — streaming-STT WebSocket turn transport.
//
// Captures PCM16 (24kHz mono) from the shared mic graph via an AudioWorklet and
// streams it to /api/sessions/:id/turns/stream while the child speaks. On
// finalize the server returns the SAME TurnEnvelope as the batch endpoint, so
// the card loop reuses applyTurnEnvelope unchanged.
//
// Failure model: every failure path (WS error/close, addModule throw, server
// error frame, backpressure, envelope timeout) resolves result() with null. The
// card loop then falls back to the batch POST using its parallel MediaRecorder
// buffer — the child never re-records.

import type { TurnEnvelope } from "../types/api.js";
import { readCSRFToken } from "./_csrf.js";
import { getAudioContext, getMicSource } from "../components/mic.js";

declare global {
  interface Window {
    __RUNASPRIEKS_STREAMING_STT__?: boolean;
    __RUNASPRIEKS_WORKLET_URL__?: string;
  }
}

const WS_OPEN_TIMEOUT_MS = 1500; // abandon streaming if the socket won't open
const ENVELOPE_TIMEOUT_MS = 12_000; // after finalize, wait this long for the turn
const MAX_BUFFERED_BYTES = 1 << 20; // 1 MiB: a stalled socket → fall back (no frame dropping)

/** Result of a successful streaming turn (envelope + server timing legs). */
export interface StreamTurnResult {
  envelope: TurnEnvelope;
  serverSttMs?: number;
  serverLlmMs?: number;
  serverTotalMs?: number;
}

/** A live streaming turn. result() resolves with the envelope or null (failed). */
export interface TurnStream {
  finalize(): void;
  /** Speculative-eval: the end-of-speech silence hold has begun (best-effort). */
  signalPause(): void;
  /** Speculative-eval: the child resumed speaking before finalize (best-effort). */
  signalResume(): void;
  result(): Promise<StreamTurnResult | null>;
  abort(): void;
}

interface ServerEnvelopeMsg {
  type: "envelope";
  envelope: TurnEnvelope;
  timing?: { stt_ms?: number; llm_ms?: number; total_ms?: number };
}
interface ServerErrorMsg {
  type: "error";
  code?: string;
  message?: string;
}
type ServerMsg = ServerEnvelopeMsg | ServerErrorMsg | { type: string };

/** isStreamingSupported gates the whole streaming attempt (cheap, sync). */
export function isStreamingSupported(): boolean {
  return (
    window.__RUNASPRIEKS_STREAMING_STT__ === true &&
    typeof window.__RUNASPRIEKS_WORKLET_URL__ === "string" &&
    window.__RUNASPRIEKS_WORKLET_URL__.length > 0 &&
    typeof AudioWorkletNode !== "undefined" &&
    typeof WebSocket !== "undefined"
  );
}

/**
 * warmStreamingWorklet pre-loads the PCM worklet module into the shared
 * AudioContext so the first turn's openTurnStream doesn't pay the module fetch
 * on the critical path between the prompt ending and the mic capturing.
 * Best-effort and idempotent — safe to call right after acquireMic at preflight.
 * No-op when streaming is unsupported or the context isn't ready yet.
 */
export async function warmStreamingWorklet(): Promise<void> {
  if (!isStreamingSupported()) return;
  const audioCtx = getAudioContext();
  const workletURL = window.__RUNASPRIEKS_WORKLET_URL__;
  if (!audioCtx || !workletURL || !audioCtx.audioWorklet) return;
  try {
    if (audioCtx.state === "suspended") {
      try { await audioCtx.resume(); } catch { /* best effort */ }
    }
    await audioCtx.audioWorklet.addModule(workletURL);
  } catch { /* best effort — openTurnStream retries and falls back if needed */ }
}

/**
 * openTurnStream sets up the worklet tap + WebSocket and returns IMMEDIATELY —
 * it does NOT wait for the socket to finish opening. Capture starts at once: the
 * worklet taps the shared mic source and PCM frames produced before the socket
 * is open are BUFFERED, then flushed (right after the CSRF `start` handshake)
 * the moment it opens. This keeps the per-turn WS round-trip off the critical
 * path between the prompt ending and the mic capturing, AND ensures the child's
 * first word is never dropped. Any setup/open failure resolves result() with
 * null so the caller falls back to the batch POST (its parallel MediaRecorder
 * buffer). The mic must already be acquired (acquireMic).
 */
export async function openTurnStream(sessionId: string): Promise<TurnStream> {
  const audioCtx = getAudioContext();
  const source = getMicSource();
  const workletURL = window.__RUNASPRIEKS_WORKLET_URL__;
  if (!audioCtx || !source || !workletURL || !audioCtx.audioWorklet) {
    throw new Error("turn_stream: streaming prerequisites missing");
  }

  // The worklet only runs in a running context (Safari suspends until gesture).
  if (audioCtx.state === "suspended") {
    try { await audioCtx.resume(); } catch { /* best effort */ }
  }
  // addModule is idempotent for the same URL; throws if the worklet 404s.
  // Pre-warmed at preflight (warmStreamingWorklet), so this is ~free per turn —
  // the only await left before capture starts.
  await audioCtx.audioWorklet.addModule(workletURL);

  const proto = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(
    `${proto}://${location.host}/api/sessions/${encodeURIComponent(sessionId)}/turns/stream`,
  );
  ws.binaryType = "arraybuffer";

  let settled = false;
  let resolveResult!: (r: StreamTurnResult | null) => void;
  const resultPromise = new Promise<StreamTurnResult | null>((res) => { resolveResult = res; });
  let envelopeTimer: number | undefined;
  let openTimer: number | undefined;
  let frameDisconnected = false;

  // Pre-open frame buffer: worklet frames produced before the socket opens are
  // queued here (not dropped) and flushed on open. Bounded by MAX_BUFFERED_BYTES
  // — a socket that never opens trips the open watchdog below and falls back.
  let wsOpen = false;
  let finalizePending = false;
  const pending: ArrayBuffer[] = [];
  let pendingBytes = 0;

  // Worklet node → muted gain → destination keeps the processor alive without
  // audible monitoring (Safari GCs output-less nodes).
  const node = new AudioWorkletNode(audioCtx, "pcm16-worklet");
  const sink = audioCtx.createGain();
  sink.gain.value = 0;
  node.connect(sink);
  sink.connect(audioCtx.destination);

  const disconnectFrames = (): void => {
    if (frameDisconnected) return;
    frameDisconnected = true;
    try { source.disconnect(node); } catch { /* ignore */ }
    node.port.onmessage = null;
  };
  const teardown = (): void => {
    disconnectFrames();
    try { node.disconnect(); } catch { /* ignore */ }
    try { sink.disconnect(); } catch { /* ignore */ }
    if (envelopeTimer) window.clearTimeout(envelopeTimer);
    if (openTimer) window.clearTimeout(openTimer);
    pending.length = 0;
    pendingBytes = 0;
  };
  const fail = (reason: string): void => {
    if (settled) return;
    settled = true;
    console.warn("[turn] stream failed → batch fallback", reason);
    teardown();
    try { ws.close(); } catch { /* ignore */ }
    resolveResult(null);
  };
  const succeed = (r: StreamTurnResult): void => {
    if (settled) return;
    settled = true;
    teardown();
    try { ws.close(); } catch { /* ignore */ }
    resolveResult(r);
  };

  node.port.onmessage = (e: MessageEvent): void => {
    const buf = e.data as ArrayBuffer;
    if (wsOpen) {
      if (ws.readyState !== WebSocket.OPEN) return;
      if (ws.bufferedAmount > MAX_BUFFERED_BYTES) {
        fail("backpressure"); // stalled socket — STT can't tolerate dropped frames
        return;
      }
      ws.send(buf);
      return;
    }
    // Socket still opening — buffer the onset frame instead of dropping it.
    pendingBytes += buf.byteLength;
    if (pendingBytes > MAX_BUFFERED_BYTES) {
      fail("preopen buffer overflow");
      return;
    }
    pending.push(buf);
  };
  source.connect(node); // capture starts NOW; frames buffer until the ws opens

  // Open watchdog: abandon streaming (→ batch fallback) if the socket won't open.
  openTimer = window.setTimeout(() => { if (!wsOpen) fail("ws open timeout"); }, WS_OPEN_TIMEOUT_MS);

  ws.addEventListener("open", () => {
    if (settled) return;
    if (openTimer) { window.clearTimeout(openTimer); openTimer = undefined; }
    wsOpen = true;
    // CSRF echo (the WS handshake can't carry custom headers) — must precede frames.
    try { ws.send(JSON.stringify({ type: "start", csrf: readCSRFToken() })); } catch { /* ignore */ }
    // Flush the buffered onset frames in order, then go live.
    for (const b of pending) {
      try { ws.send(b); } catch { /* ignore */ }
    }
    pending.length = 0;
    pendingBytes = 0;
    // A finalize that arrived before the socket opened is sent after the flush.
    if (finalizePending) {
      try { ws.send(JSON.stringify({ type: "finalize" })); } catch { /* ignore */ }
    }
  }, { once: true });

  ws.addEventListener("message", (e: MessageEvent): void => {
    if (typeof e.data !== "string") return; // ignore unexpected binary
    let msg: ServerMsg;
    try { msg = JSON.parse(e.data) as ServerMsg; } catch { return; }
    if (msg.type === "envelope") {
      const m = msg as ServerEnvelopeMsg;
      if (m.envelope) {
        succeed({
          envelope: m.envelope,
          serverSttMs: m.timing?.stt_ms,
          serverLlmMs: m.timing?.llm_ms,
          serverTotalMs: m.timing?.total_ms,
        });
      }
    } else if (msg.type === "error") {
      fail("server:" + ((msg as ServerErrorMsg).code ?? "unknown"));
    }
    // type === "interim": ignored (best-effort UX, not wired yet)
  });
  ws.addEventListener("close", () => fail("ws closed"));
  ws.addEventListener("error", () => fail("ws error"));

  return {
    finalize(): void {
      disconnectFrames(); // stop streaming PCM
      if (wsOpen && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: "finalize" })); } catch { /* ignore */ }
      } else {
        // Socket not open yet — send finalize right after the start+flush on open.
        finalizePending = true;
      }
      // Watchdog: no envelope within budget → fall back to batch.
      envelopeTimer = window.setTimeout(() => fail("envelope timeout"), ENVELOPE_TIMEOUT_MS);
    },
    // signalPause/signalResume are best-effort speculative-eval hints — the
    // server starts/cancels an LLM eval on the interim transcript during the
    // hold. Dropped silently if the socket isn't open (speculation just doesn't
    // happen that turn; finalize still drives the authoritative evaluation).
    signalPause(): void {
      if (settled || !wsOpen || ws.readyState !== WebSocket.OPEN) return;
      try { ws.send(JSON.stringify({ type: "settling" })); } catch { /* ignore */ }
    },
    signalResume(): void {
      if (settled || !wsOpen || ws.readyState !== WebSocket.OPEN) return;
      try { ws.send(JSON.stringify({ type: "resumed" })); } catch { /* ignore */ }
    },
    result(): Promise<StreamTurnResult | null> { return resultPromise; },
    abort(): void { fail("abort"); },
  };
}
